import { useEffect, useMemo, useState } from 'react'
import { ViewsStudio } from './ViewsStudio'
import { apiOrigin, createViewsApi, startDemoExploreSession, TOKEN_KEY, type Company } from './viewsApi'
import type { ViewSpec } from './viewModel'

const REFRESH_TOKEN_KEY = 'fusegrid_api_refresh_token'

/**
 * The standalone Views route (/views). Opened from the Explore hub, from the
 * Excel pane's "Open in browser" drill-through (?fg_explore=<token>, which
 * prefills the builder with the selected number's governed spec), or with
 * ?company=<company_key>.
 */
export function ViewsPage() {
  const params = useMemo(() => new URLSearchParams(window.location.search), [])
  const [token, setToken] = useState<string | null>(null)
  const [companies, setCompanies] = useState<Company[] | null>(null)
  const [initial, setInitial] = useState<{ companyKey: string | null; spec: ViewSpec | null }>({ companyKey: params.get('company'), spec: null })
  const [error, setError] = useState<string | null>(null)

  useEffect(() => {
    let live = true
    const exploreToken = params.get('fg_explore')
    ;(async () => {
      let access = ''
      if (exploreToken) {
        const demo = await startDemoExploreSession({ origin: apiOrigin(), token: exploreToken })
        if (demo) {
          // A shared-demo drill-through gets its own isolated session.
          window.sessionStorage.setItem(TOKEN_KEY, demo.accessToken)
          window.sessionStorage.setItem(REFRESH_TOKEN_KEY, demo.refreshToken)
          access = demo.accessToken
        }
      }
      access ||= window.localStorage.getItem(TOKEN_KEY) || ''
      if (!access) {
        window.location.replace(`/login?next=${encodeURIComponent(`${window.location.pathname}${window.location.search}`)}`)
        return
      }
      if (live) setToken(access)
    })().catch((e) => live && setError(String(e?.message || e)))
    return () => {
      live = false
    }
  }, [params])

  const api = useMemo(() => (token ? createViewsApi({ token: () => token }) : null), [token])

  useEffect(() => {
    if (!api) return
    let live = true
    const exploreToken = params.get('fg_explore')
    Promise.all([api.companies(), exploreToken ? api.resolveExploreContext(exploreToken) : Promise.resolve(null)])
      .then(([list, context]) => {
        if (!live) return
        if (!list.length) throw new Error('No authorized FuseGrid companies are available.')
        if (context) setInitial({ companyKey: context.company_key, spec: context.semantic_spec })
        setCompanies(list)
      })
      .catch((e) => live && setError(String(e?.message || e)))
    return () => {
      live = false
    }
  }, [api, params])

  return (
    <main className="fg-views-page" style={{ maxWidth: 1200, margin: '0 auto', padding: 24 }}>
      <header style={{ display: 'flex', alignItems: 'baseline', gap: 12 }}>
        <h1 style={{ margin: 0 }}>Views</h1>
        <span className="fg-views-muted">Build a governed view by hand or by describing it, then link it into Excel.</span>
      </header>
      {error && <p className="fg-views-status error" role="alert">{error}</p>}
      {api && companies && <ViewsStudio api={api} companies={companies} initialCompanyKey={initial.companyKey} initialSpec={initial.spec} />}
      {!error && !companies && <p className="fg-views-muted">Loading…</p>}
    </main>
  )
}

export default ViewsPage
